import { createApp, shallowReactive, watch } from 'vue';
import { createPinia } from 'pinia';
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { far } from '@fortawesome/free-regular-svg-icons'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { fr } from 'vuetify/locale'
import { App, getRouter, vuetifyOptions, type IConfiguration, type IProfileTab } from '@fondationcss/ui';
import { useSdk } from '@fondationcss/sdk';
import { getCustomRoutes } from './routes';
import { sidebarItems } from './sidebar-items';
import SystemAdminTab from './components/SystemAdminTab.vue';
import { WikiPlugin } from '@fondationcss/wiki'

import 'vuetify/styles';
import '@fontsource/public-sans/400.css';
import '@fontsource/public-sans/500.css';
import '@fontsource/public-sans/600.css';
import '@fontsource/public-sans/700.css';
import '@fondationcss/ui/dist/style.css';
import '@fondationcss/wiki/dist/style.css';
import '@fondationcss/markdown/dist/markdown.css';
import "@/styles.css";
import 'highlight.js/styles/github.css';


const config = {
  AppName: 'Byrnu Perspective',
  SiteNameShort: 'Byrnu Perspective',
  SiteNameLong: 'Byrnu Perspective',
  SiteLogo: "/perspective-logo.svg",
  SiteLogoAlt: '/perspective-logo-white.svg',
  LogoLine1: 'Byrnu',
  LogoLine2: 'Perspective',
  SidebarItems: sidebarItems,
  Logo: '/byrnu-logo.svg',
  Copyright: `© ${new Date().getFullYear()} Byrnu inc.`,
  CopyrightUrl: 'https://byrnu.com/?utm_source=byrnu-perspective',
  DefaultLayout: 'byrnu-red-app',
  ThemeColor: '#3460a8',
} as IConfiguration;

const vuetify = createVuetify({
    components,
    directives,
    locale: {
      locale: 'fr',
      messages: { fr }
    },
    ...vuetifyOptions
  })
const app = createApp(App);
import Gantt from "@fondationcss/gantt";
app.use(Gantt); // default export
const router = getRouter(getCustomRoutes as () => []);
app.provide('config', config)
app.provide('sdkOptions', { apiUrl: import.meta.env.VITE_API_URL })

// Section cachée « Administration système » : onglet injecté dans le popup de
// profil, visible uniquement pour les administrateurs. On garde la liste des
// onglets synchronisée avec le rôle courant (sdk.me est chargé de façon
// asynchrone après le bootstrap), de sorte que l'onglet n'apparaît jamais pour
// les non-administrateurs.
const systemAdminTab: IProfileTab = {
  value: 'perspective-system-admin',
  icon: 'fas fa-user-gear',
  label: 'Administration système',
  component: SystemAdminTab,
  insertLocation: '222', // insère avant l'onglet « Paramètres »
};
const profileTabs = shallowReactive<IProfileTab[]>([]);
const sdk = useSdk();
watch(
  () => sdk.me.value?.roles,
  (roles) => {
    const isAdmin = roles?.includes('Perspective.Administrator') ?? false;
    const index = profileTabs.findIndex(t => t.value === systemAdminTab.value);
    if (isAdmin && index === -1) {
      profileTabs.push(systemAdminTab);
    } else if (!isAdmin && index !== -1) {
      profileTabs.splice(index, 1);
    }
  },
  { immediate: true }
);
app.provide('profileTabs', profileTabs);
app.use(vuetify);
app.component('font-awesome-icon', FontAwesomeIcon)
library.add(fas)
library.add(far)
app.use(createPinia());
app.use(PerfectScrollbarPlugin);
app.use(router);
app.use(WikiPlugin, { router })
app.mount('#app');